import { useMemo } from 'react';
import type { AgentRunSnapshot, AgentTaskNode, Citation, DocEditRecoveryResult, DocumentVersionSummary, EditBatch, HtmlEditApplyResult, ReasoningSegment, TaskNodeStatus } from '@corner/shared';
import CoTContainer from './cot/CoTContainer';
import type { ActivityPhase, ActivityStatus, CoTStepData, CoTErrorRenderContext } from './cot/types';
import type { StepStatus } from './cot/CoTStepIndicator';
import type { CoTAction } from './cot/CoTAnswerBlock';
import type { NodeActionDenial } from '../../hooks/useQueryRunState';
import { getRetryDenialMessage } from '../../lib/retryDenialPresentation';
import { formatQueryRunContextFromSnapshot } from '../../lib/formatQueryRunContext';
import { APP } from './tokens';

const F = APP.fontFamily;

// ─── Status mapping ──────────────────────────────

function mapNodeStatus(status: TaskNodeStatus): StepStatus {
  switch (status) {
    case 'completed':
      return 'done';
    case 'running':
    case 'replanning':
    case 'retry_pending':
      return 'active';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'skipped';
    case 'blocked':
      return 'blocked';
    case 'pending':
    case 'cancelled':
    default:
      return 'pending';
  }
}

function mapRunStatus(snapshot: AgentRunSnapshot): 'working' | 'done' | 'error' {
  switch (snapshot.status) {
    case 'completed':
    case 'partial':
      return 'done';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'done';
    default:
      return 'working';
  }
}

// ─── Node → CoTStepData adapter ──────────────────

function nodeToStep(node: AgentTaskNode, denial?: NodeActionDenial, userPlan?: 'free' | 'pro' | 'enterprise'): CoTStepData {
  const maxRetries = node.maxRetries ?? 3;
  const retryCount = node.retryCount ?? 0;

  const step: CoTStepData = {
    title: node.label,
    status: mapNodeStatus(node.status),
  };

  if (node.status === 'skipped') {
    step.description = 'Skipped by user';
  } else if (node.status === 'blocked') {
    step.description = 'Blocked by failed dependency';
  } else if (node.status === 'retry_pending') {
    step.description = `Retrying... (attempt ${retryCount}/${maxRetries})`;
  } else if (node.result && node.status === 'completed') {
    step.description = node.result.length > 120 ? `${node.result.slice(0, 120)}…` : node.result;
  }

  if (node.status === 'failed' && node.error) {
    step.errorMessage = node.error;
    step.retryable = node.retryable === true && retryCount < maxRetries;
    step.skippable = node.skippable === true;
    if (step.retryable && maxRetries > 1) {
      step.retryCountLabel = `${retryCount + 1}/${maxRetries}`;
    }
    if (denial) {
      step.denialMessage = getRetryDenialMessage(denial.code, userPlan);
      if (denial.action === 'retry') step.retryable = false;
      if (denial.action === 'skip') step.skippable = false;
    }
  }

  return step;
}

// ─── Partial completion detection ────────────────

function hasPartialCompletion(snapshot: AgentRunSnapshot): boolean {
  if (snapshot.status !== 'completed' && snapshot.status !== 'partial') return false;
  if (!snapshot.finalAnswer) return false;
  return snapshot.tasks.some(
    (t) => t.status === 'failed' || t.status === 'skipped' || t.status === 'blocked',
  );
}

// ─── Citation component ──────────────────────────

function CitationList({ citations }: { citations: Citation[] }) {
  if (citations.length === 0) return null;
  // Deduplicate by documentName + sectionHeading
  const unique = citations.reduce<Citation[]>((acc, c) => {
    if (!acc.some((e) => e.documentName === c.documentName && e.sectionHeading === c.sectionHeading)) {
      acc.push(c);
    }
    return acc;
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 6 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: '#8B7D6B', fontFamily: F, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Sources
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {unique.slice(0, 8).map((c) => (
          <span
            key={c.citationId}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 10,
              fontWeight: 500,
              color: '#6B6560',
              backgroundColor: 'rgba(212, 106, 58, 0.06)',
              padding: '2px 8px',
              borderRadius: 4,
              fontFamily: F,
              lineHeight: '16px',
            }}
          >
            {c.documentName}
            {c.sectionHeading && (
              <span style={{ color: '#9B8E83' }}>· {c.sectionHeading}</span>
            )}
            {c.pageNumber != null && (
              <span style={{ color: '#9B8E83' }}>p.{c.pageNumber}</span>
            )}
          </span>
        ))}
        {unique.length > 8 && (
          <span style={{ fontSize: 10, color: '#9B8E83', fontFamily: F, padding: '2px 4px' }}>
            +{unique.length - 8} more
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Props ──────────────────────────────────────

interface QueryRunPanelProps {
  snapshot: AgentRunSnapshot;
  /** Live-streamed partial answer (before FINAL_ANSWER). */
  streamingAnswer?: string;
  /** Thinking segments from the run. */
  thinkingSegments?: ReasoningSegment[];
  onRetryNode: (nodeId: string) => void;
  onSkipNode: (nodeId: string) => void;
  isNodeActionPending: (nodeId: string) => boolean;
  getNodeDenial?: (nodeId: string) => NodeActionDenial | undefined;
  errorRenderContext?: CoTErrorRenderContext;
  answerActions?: CoTAction[];
  /** Merge staged edit batches from app CoT state (preserved across V2 snapshot sync). */
  mergeEditBatchesFromSteps?: CoTStepData[];
  onEditBatchChange?: (batch: EditBatch) => void;
  onEditBatchVersionReady?: (batch: EditBatch, htmlRevision?: HtmlEditApplyResult) => void;
  onEditBatchRecoveryVersionReady?: (result: DocEditRecoveryResult) => void;
  documentVersions?: DocumentVersionSummary[];
  onSelectDocumentVersion?: (version: DocumentVersionSummary) => void;
  getToken?: () => string | null;
  editArtifactFormat?: 'source' | 'pdf';
}

// ─── Component ───────────────────────────────────

export default function QueryRunPanel({
  snapshot,
  streamingAnswer,
  thinkingSegments,
  onRetryNode,
  onSkipNode,
  isNodeActionPending,
  getNodeDenial,
  errorRenderContext,
  answerActions,
  mergeEditBatchesFromSteps,
  onEditBatchChange,
  onEditBatchVersionReady,
  onEditBatchRecoveryVersionReady,
  documentVersions,
  onSelectDocumentVersion,
  getToken,
  editArtifactFormat = 'pdf',
}: QueryRunPanelProps) {
  const status = mapRunStatus(snapshot);
  const partialCompletion = hasPartialCompletion(snapshot);

  const steps = useMemo<CoTStepData[]>(() => {
    const base = snapshot.tasks.map((node) => ({
      ...nodeToStep(node, getNodeDenial?.(node.id), errorRenderContext?.userPlan),
      agentTaskId: node.id,
    }));
    const merge = mergeEditBatchesFromSteps;
    if (!merge?.length) return base;
    const byId = new Map(
      merge
        .filter((s): s is CoTStepData & { agentTaskId: string } => Boolean(s.agentTaskId))
        .map((s) => [s.agentTaskId, s]),
    );
    return base.map((step) => {
      const bid = step.agentTaskId;
      if (!bid) return step;
      const m = byId.get(bid);
      return m?.editBatch ? { ...step, editBatch: m.editBatch } : step;
    });
  }, [snapshot.tasks, getNodeDenial, errorRenderContext?.userPlan, mergeEditBatchesFromSteps]);

  const handleRetry = useMemo(() => {
    return (stepIndex: number) => {
      const node = snapshot.tasks[stepIndex];
      if (!node || !node.retryable || isNodeActionPending(node.id)) return;
      onRetryNode(node.id);
    };
  }, [snapshot.tasks, onRetryNode, isNodeActionPending]);

  const handleSkip = useMemo(() => {
    return (stepIndex: number) => {
      const node = snapshot.tasks[stepIndex];
      if (!node || !node.skippable || isNodeActionPending(node.id)) return;
      onSkipNode(node.id);
    };
  }, [snapshot.tasks, onSkipNode, isNodeActionPending]);

  // Use streaming answer while live, fall back to finalized answer from snapshot
  const isStreaming = !!streamingAnswer && !snapshot.finalAnswer;
  const answerContent = snapshot.finalAnswer ?? (streamingAnswer || undefined);
  const answer = answerContent
    ? { content: answerContent, actions: snapshot.finalAnswer ? answerActions : undefined }
    : undefined;
  const runContextUnderstanding = formatQueryRunContextFromSnapshot(snapshot);

  const retryHandler = snapshot.tasks.some((t) => t.status === 'failed' && t.retryable)
    ? handleRetry : undefined;
  const skipHandler = snapshot.tasks.some((t) => t.status === 'failed' && t.skippable)
    ? handleSkip : undefined;

  /** Mirror pre-snapshot query-run UI: stream STRATEGY/OBSERVATION/etc. inside the CoT card (not only above it). */
  const reasoningActivityPhases = useMemo((): ActivityPhase[] | undefined => {
    if (!thinkingSegments?.length) return undefined;
    const nonEmpty = thinkingSegments.filter((s) => (s.text ?? '').trim().length > 0);
    if (!nonEmpty.length) return undefined;
    const visible = nonEmpty.length > 15 ? nonEmpty.slice(-15) : nonEmpty;
    const stepStatus: ActivityStatus = status === 'working' ? 'active' : 'success';
    return [{
      id: 'v2-query-run-reasoning',
      collapsed: false,
      steps: visible.map((segment, index) => ({
        id: `v2-think-${segment.taskId}-${segment.at}-${index}`,
        kind: 'thinking' as const,
        label: segment.type,
        body: segment.text,
        status: stepStatus,
        expanded: true,
      })),
    }];
  }, [thinkingSegments, status]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Main CoT container — reasoning segments + answer stream live in the same card */}
      <CoTContainer
        status={status}
        steps={steps}
        understanding={runContextUnderstanding}
        activityPhases={reasoningActivityPhases}
        answer={answer}
        onRetryStep={retryHandler}
        onSkipStep={skipHandler}
        errorRenderContext={errorRenderContext}
        partialCompletion={partialCompletion}
        answerStreaming={isStreaming}
        onEditBatchChange={onEditBatchChange}
        onEditBatchVersionReady={onEditBatchVersionReady}
        onEditBatchRecoveryVersionReady={onEditBatchRecoveryVersionReady}
        documentVersions={documentVersions}
        onSelectDocumentVersion={onSelectDocumentVersion}
        getToken={getToken}
        editArtifactFormat={editArtifactFormat}
      />

      {/* Citations (below answer, shown after completion) */}
      {(status === 'done' || isStreaming) && snapshot.citations.length > 0 && (
        <CitationList citations={snapshot.citations} />
      )}
    </div>
  );
}
